import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { PageHeader, TWButton } from '../../components/ui';
import platformApi from '../../services/platformApi';
import HealthStrip from './health-strip';
import PlatformTable from './platform-table';

// Same URL keys platform-table.js reads and writes for its date-range
// picker — this page only reads them, never writes, so there is a single
// writer (the picker) and no risk of the two fighting over the same params.
// Absent means "this month so far", computed fresh rather than written back,
// mirroring platform-table.js's own defaultFrom/defaultTo.
const FROM_PARAM = 'from';
const TO_PARAM = 'to';

const defaultFrom = () => dayjs().startOf('month');
const defaultTo = () => dayjs();

// The API's inclusive UTC-range convention (see platformApi.js's
// financeQuery comment): day boundaries turned into UTC instants, the same
// way connect-period.js's toPeriodQuery does for Connect's own date-ranged
// reads.
const toQuery = (from, to) => ({
  from: from ? from.startOf('day').toISOString() : undefined,
  to: to ? to.endOf('day').toISOString() : undefined,
});

// Task B1: route + service layer + shell. Task B2: the health strip. This
// task (B3) is the one that actually fetches and renders both halves of the
// page spec describes — "Üstte sağlık şeridi (bugün), altında tarih
// aralıklı finans tablosu (dönem)": health is "right now" and only reloads
// on mount or Refresh; the company table is period-scoped and reloads
// whenever the URL's from/to change.
//
// Task 4 (admin Finance console): the body is its own export so the
// console's Revenue tab can mount it without a page shell or PageHeader of
// its own — the console already draws the page header. The default export
// below stays the standalone route for the redirect release, keeping its
// header and shell exactly as before.
export const PlatformFinanceBody = () => {
  const [searchParams] = useSearchParams();
  const fromParam = searchParams.get(FROM_PARAM);
  const toParam = searchParams.get(TO_PARAM);

  const [health, setHealth] = useState(null);

  const [finance, setFinance] = useState({ rows: [], totals: null });
  const [financeLoading, setFinanceLoading] = useState(true);
  const [financeError, setFinanceError] = useState('');

  // Guards against a response landing after a newer request has already
  // started — the same requestId idiom connect-finance.js uses for its own
  // per-section loads.
  const requestId = useRef(0);

  // A failed read (network/5xx) is folded into the same `readable: false`
  // shape toPlatformHealth already produces for a body the API itself
  // marks unreadable — HealthStrip's amber "Sağlık kontrol edilemedi"
  // branch is the one honest place for either kind of failure, never the
  // neutral loading placeholder forever, and never green.
  const loadHealth = useCallback(async () => {
    try {
      const next = platformApi.toPlatformHealth(await platformApi.getHealth());
      setHealth(next || { readable: false, reason: null, hasHardSignal: false, hardSignals: [] });
    } catch {
      setHealth({ readable: false, reason: null, hasHardSignal: false, hardSignals: [] });
    }
  }, []);

  const loadFinance = useCallback(() => {
    const from = fromParam ? dayjs(fromParam) : defaultFrom();
    const to = toParam ? dayjs(toParam) : defaultTo();
    const { from: fromIso, to: toIso } = toQuery(from, to);

    requestId.current += 1;
    const id = requestId.current;
    setFinanceLoading(true);
    setFinanceError('');

    platformApi
      .getFinance(fromIso, toIso)
      .then((res) => {
        if (id !== requestId.current) return;
        setFinance({ rows: platformApi.toPlatformRows(res), totals: platformApi.toPlatformTotals(res) });
        setFinanceLoading(false);
      })
      .catch(() => {
        if (id !== requestId.current) return;
        setFinanceError('Could not load finance data.');
        setFinanceLoading(false);
      });
  }, [fromParam, toParam]);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  useEffect(() => {
    loadFinance();
  }, [loadFinance]);

  const refresh = () => {
    loadHealth();
    loadFinance();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <TWButton size="sm" icon={ArrowPathIcon} label="Refresh" onClick={refresh} />
      </div>

      <HealthStrip health={health} />

      <PlatformTable
        rows={finance.rows}
        totals={finance.totals}
        loading={financeLoading}
        error={financeError}
        onRetry={loadFinance}
      />
    </div>
  );
};

const PlatformFinance = () => (
  <div className="min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
    <div className="mx-auto max-w-6xl space-y-8">
      <PageHeader
        title="Platform Finance"
        description="Revenue and Connect health across every company, legacy and Connect Direct alike."
      />
      <PlatformFinanceBody />
    </div>
  </div>
);

export default PlatformFinance;
